import { cn } from "@/lib/utils";
import type { DREData } from "@/hooks/useDREReport";

interface DRETableProps {
  data: DREData;
}

function formatCurrency(value: number): string {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

interface RowProps {
  label: string;
  value: number;
  isTotal?: boolean;
  isSubtotal?: boolean;
  isNegative?: boolean;
  indent?: number;
  percent?: number;
  className?: string;
}

function DRERow({
  label,
  value,
  isTotal,
  isSubtotal,
  isNegative,
  indent = 0,
  percent,
  className,
}: RowProps) {
  const isHighlight = isTotal || isSubtotal;
  const displayValue = isNegative ? -Math.abs(value) : value;

  return (
    <tr
      className={cn(
        "border-b border-border/50",
        isTotal && "bg-primary/10 font-bold",
        isSubtotal && "bg-muted/50 font-semibold",
        className
      )}
    >
      <td
        className={cn("py-2 px-4", isHighlight && "font-semibold")}
        style={{ paddingLeft: `${1 + indent * 1.5}rem` }}
      >
        {isNegative && !isHighlight && "(−) "}
        {label}
      </td>
      <td
        className={cn(
          "py-2 px-4 text-right font-mono",
          value < 0 && "text-destructive",
          value > 0 && isTotal && "text-green-600 dark:text-green-400"
        )}
      >
        {formatCurrency(displayValue)}
      </td>
      <td className="py-2 px-4 text-right font-mono text-muted-foreground w-[80px]">
        {percent !== undefined ? formatPercent(percent) : ""}
      </td>
    </tr>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <tr className="bg-muted">
      <td colSpan={3} className="py-2 px-4 font-bold text-sm uppercase tracking-wide">
        {title}
      </td>
    </tr>
  );
}

export function DRETable({ data }: DRETableProps) {
  const {
    grossRevenue,
    deductions,
    netRevenue,
    cmv,
    grossProfit,
    grossMarginPercent,
    operatingExpenses,
    operatingResult,
    operatingMarginPercent,
    financialExpenses,
    netProfit,
    netMarginPercent,
  } = data;

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted border-b">
            <th className="py-3 px-4 text-left font-semibold">Descrição</th>
            <th className="py-3 px-4 text-right font-semibold w-[150px]">Valor</th>
            <th className="py-3 px-4 text-right font-semibold w-[80px]">%</th>
          </tr>
        </thead>
        <tbody>
          {/* RECEITA BRUTA */}
          <SectionHeader title="Receita Bruta" />
          <DRERow label="Vendas de Produtos" value={grossRevenue.productSales} indent={1} />
          <DRERow label="Taxa de Serviço" value={grossRevenue.serviceTax} indent={1} />
          <DRERow label="Taxa de Entrega" value={grossRevenue.deliveryFees} indent={1} />
          <DRERow label="Total Receita Bruta" value={grossRevenue.total} isSubtotal />

          {/* DEDUÇÕES */}
          <SectionHeader title="Deduções" />
          <DRERow label="Descontos Concedidos" value={deductions.discounts} indent={1} isNegative />
          <DRERow label="Cancelamentos" value={deductions.cancellations} indent={1} isNegative />
          <DRERow label="Total Deduções" value={-deductions.total} isSubtotal />

          {/* RECEITA LÍQUIDA */}
          <DRERow
            label="RECEITA LÍQUIDA"
            value={netRevenue}
            isTotal
            percent={100}
          />

          {/* CMV */}
          <SectionHeader title="Custos Variáveis (CMV)" />
          <DRERow label="Custo de Mercadoria Vendida" value={cmv.total} indent={1} isNegative />
          <DRERow label="Total CMV" value={-cmv.total} isSubtotal />

          {/* LUCRO BRUTO */}
          <DRERow
            label="LUCRO BRUTO"
            value={grossProfit}
            isTotal
            percent={grossMarginPercent}
          />

          {/* DESPESAS OPERACIONAIS */}
          <SectionHeader title="Despesas Operacionais" />
          {operatingExpenses.byAccount.map((item) => (
            <DRERow
              key={item.accountId}
              label={item.accountName}
              value={item.amount}
              indent={1}
              isNegative
            />
          ))}
          {operatingExpenses.byAccount.length === 0 && (
            <tr>
              <td colSpan={3} className="py-2 px-4 text-muted-foreground italic pl-8">
                Nenhuma despesa operacional no período
              </td>
            </tr>
          )}
          <DRERow label="Total Despesas Operacionais" value={-operatingExpenses.total} isSubtotal />

          {/* RESULTADO OPERACIONAL */}
          <DRERow
            label="RESULTADO OPERACIONAL (EBITDA)"
            value={operatingResult}
            isTotal
            percent={operatingMarginPercent}
          />

          {/* DESPESAS FINANCEIRAS */}
          <SectionHeader title="Despesas Financeiras" />
          <DRERow label="Taxas de Cartão" value={financialExpenses.cardFees} indent={1} isNegative />
          <DRERow label="Outras Despesas Financeiras" value={financialExpenses.otherFees} indent={1} isNegative />
          <DRERow label="Total Despesas Financeiras" value={-financialExpenses.total} isSubtotal />

          {/* LUCRO LÍQUIDO */}
          <DRERow
            label="LUCRO LÍQUIDO"
            value={netProfit}
            isTotal
            percent={netMarginPercent}
            className="text-lg"
          />
        </tbody>
      </table>
    </div>
  );
}
